<template>
  <v-dialog 
    :value="value"
    @input="$emit('input', $event)"
    transition="fade-transition" 
    scrollable 
    width="600px">
    <v-card>
      <v-card-title class="card-title-background" v-if="item">
        Checklists for {{ item.name}}
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="bg-white" style="height: 400px;margin-top:20px;">
        <v-list>
          <v-list-item v-for="(check, index) in checklist" :key="index" class="px-0">
            <v-list-item-icon class="mr-3">
              <v-progress-circular v-if="check.loading" indeterminate color="warning" size="24"></v-progress-circular>
              <v-icon v-else :color="check.passed ? 'success' : 'error'">
                {{ check.passed ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <div class="title-section">
                <v-list-item-title>{{ check.text }}</v-list-item-title>
              </div>
              <div class="value-section">
                <!-- Followers section -->
                <div v-if="(check.igFollower || check.tiktokFollower) && !isLoading" class="text-left">
                  <div class="value-item" v-if="check.igFollower">IG Followers: {{ check.igFollower | thousandSeprator}}</div>
                  <div class="value-item" v-if="check.tiktokFollower">TikTok Followers: {{ check.tiktokFollower | thousandSeprator}}</div>
                </div>
                <!-- Contact details section -->
                <div v-else-if="check.email !== undefined" class="text-left">
                  <div class="value-item">Email: {{ check.email || 'N/A' }}</div>
                  <div class="value-item">Contact No: {{ check.contactNo || 'N/A' }}</div>
                </div>
                <!-- Other values section -->
                <div v-else-if="check.value !== undefined" class="text-left">
                  <div v-if="Array.isArray(check.value)" class="value-item">
                    {{ check.value.join(', ') }}
                  </div>
                  <div v-else class="value-item">
                    {{ check.value }}
                  </div>
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="justify-end pa-4">
        <v-btn @click="close">
          Done
        </v-btn>
        <v-btn color="secondary" @click="loadChecklist(true)" :loading="verifying" :disabled="isLoading">
          Check Validation
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/* eslint-disable */
import influencerAPIs from '../influencerAPIs';
import config from './config';

export default {
  name: 'VerifyDialog',
  props: {
    value: Boolean,
    item: Object
  },
  data() {
    return {
      verifying: false,
      checklist: [],
      isLoading: true
    }
  },
  watch: {
    item: {
      async handler(newItem) {
        if (newItem && newItem.id) {
          await this.loadChecklist()
        }
      },
      deep: true
    }
  },
  computed: {
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    async checkFollowerData() {
      try {
        const result = await influencerAPIs.checkFollowers(this.item.id)
        return result
      } catch (error) {
        console.error('Error fetching public profile:', error)
        throw error
      }
    },
    async loadChecklist(force = false) {
      this.isLoading = true
      // Call generateChecklist first with null to show loading state
      this.checklist = this.generateChecklist(this.item, null)
      
      // Then fetch follower data asynchronously
      try {
        if (this.item.verify === undefined || force) {
          // Set loading to true before API call
          const followerData = await this.checkFollowerData()
          // Update only the minimum followers check with actual data
          const updatedMinimumFollowersCheck = this.generateChecklist(this.item, followerData)[0]
          updatedMinimumFollowersCheck.loading = false
          this.$set(this.checklist, 0, updatedMinimumFollowersCheck)
          this.verifyCreatorCommunity()
        } else {
          this.checklist[0].loading = false
        }
      } catch (error) {
        console.error('Error loading checklist:', error)
        // On error, show the minimum followers check without data
        const errorCheck = { ...this.checklist[0], loading: false, igFollower: 0, tiktokFollower: 0, passed: false }
        this.$set(this.checklist, 0, errorCheck)
      } finally {
        // Set loading to false after API call completes
        this.isLoading = false
      }
    },
    generateChecklist(item, followerData) {
      const targetCountries = config.targetCountries
      
      // Check if any ordinary rate is filled
      const hasOrdinaryRates = item.ordinaryRates && Object.keys(item.ordinaryRates).some(key => {
        if (key.includes('Currency')) return false
        return item.ordinaryRates[key] && item.ordinaryRates[key] > 0
      })

      // Extract follower counts from public response
      let igFollowerCount = 0
      let tiktokFollowerCount = 0

      if (followerData) {
        if (followerData.instagram && followerData.instagram.data && followerData.instagram.data.followers) {
          igFollowerCount = followerData.instagram.data.followers
        }
        if (followerData.tiktok && followerData.tiktok.data && followerData.tiktok.data.followers) {
          tiktokFollowerCount = followerData.tiktok.data.followers
        }
      } else {
        if (item.igFollowerCount) {
          igFollowerCount = item.igFollowerCount
        }
        if (item.tiktokFollowerCount) {
          tiktokFollowerCount = item.tiktokFollowerCount
        }
      }

      return [
        {
          text: item.country === 'Singapore' ? 'Minimum 5000 followers' : 'Minimum 10,000 followers',
          loading: this.isLoading,
          passed: item.country === 'Singapore' 
            ? (igFollowerCount >= 5000) || (tiktokFollowerCount >= 5000)
            : (igFollowerCount >= 10000) || (tiktokFollowerCount >= 10000),
          igFollower: igFollowerCount,
          tiktokFollower: tiktokFollowerCount,
        },
        {
          text: 'Aligned to the country',
          passed: targetCountries.includes(item.country),
          value: item.country
        },
        {
          text: 'Clearly defined category/vertical',
          passed: item.keywords && item.keywords.length > 0,
          value: item.keywords
        },
        {
          text: 'Complete bio/description',
          passed: !!item.aboutYou,
          value: item.aboutYou
        },
        {
          text: 'Accurate contact details',
          passed: !!item.email || !!item.contactNo,
          email: item.email,
          contactNo: item.contactNo
        },
        {
          text: 'Updated rate card',
          passed: hasOrdinaryRates,
          value: hasOrdinaryRates ? 'Ratecards Present' : 'NO DATA'
        }
      ]
    },
    async verifyCreatorCommunity() {
      this.verifying = true
      try {
        // Check if all checklist items pass
        const allChecksPassed = this.checklist.every(check => check.passed)
        
        await influencerAPIs.updateCreatorCommunityById({
          id: this.item.id,
          verify: allChecksPassed
        })
        this.$emit('verified')
        // if (successDialog) {
        //   this.$swal({
        //     text: allChecksPassed ? 'Creator community verified successfully!' : 'Creator community saved with incomplete verification.',
        //     type: 'success'
        //   })
        //   this.close()
        // }
      } catch (error) {
        this.$swal({
          text: 'Error saving creator community.',
          type: 'error'
        })
      } finally {
        this.verifying = false
      }
    }
  }
}
</script>

<style scoped>
.card-title-background {
  background-color: #3F3FFF;
}

.list-item-wrapper {
  display: flex !important;
  gap: 16px;
}

.title-section {
  flex: 1;
  min-width: 0;
}

.value-section {
  flex: 1;
  text-align: right;
  padding-right: 8px;
}

.value-item {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// VerifyDialog.vue?d56b4ab6